import React, { useState } from 'react';
import FloorPlan from './FloorPlan';
import BookingPanel from './BookingPanel';
import RoomList from './RoomList';
import DatePicker from './DatePicker';

/**
 * MobileView - Phone layout accessed via QR scan or direct link.
 *
 * Features:
 * - Toggle between floor plan map view and list view
 * - Date picker to view availability for any future date
 * - List view groups rooms by type with availability and quick-book
 * - Handles QR deep links from kiosk (?room=xxx&date=xxx&start=xxx)
 * - Responsive across phone sizes
 */
export default function MobileView({
  officeConfig,
  availability,
  selectedRoom,
  selectedDate,
  onDateChange,
  onRoomSelect,
  onBook,
  user,
  onLogin,
  onLogout,
  loading,
  error,
  onDismissError,
  bookingSuccess,
  onDismissSuccess,
  existingBooking,
  directBooking,
}) {
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'map'

  if (!user) {
    return (
      <div className="mobile-view">
        <header className="mobile-header">
          <div className="mobile-header-top">
            <div className="mobile-header-brand">
              <img src={officeConfig.theme?.logoIcon || '/logo-icon.png'} alt={officeConfig.theme?.logoAlt || 'SSP'} className="mobile-header-logo" />
              <span className="mobile-subtitle">{officeConfig.name} Office</span>
            </div>
          </div>
        </header>
        <div className="login-gate">
          <img src={officeConfig.theme?.logoLight || '/logo-color.png'} alt={officeConfig.theme?.logoAlt || 'SSP Innovations'} className="login-gate-logo" />
          <h2>Room Booking</h2>
          <p>Sign in with your SSP account to view availability and book spaces.</p>
          <button className="login-btn login-btn-large" onClick={onLogin}>
            Sign In with Microsoft
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-view">
      {/* Mobile Header */}
      <header className="mobile-header">
        <div className="mobile-header-top">
          <div className="mobile-header-brand">
            <img src={officeConfig.theme?.logoIcon || '/logo-icon.png'} alt={officeConfig.theme?.logoAlt || 'SSP'} className="mobile-header-logo" />
            <span className="mobile-subtitle">{officeConfig.name} Office</span>
          </div>
          <div className="mobile-user">
            <span className="mobile-user-name">{user.name?.split(' ')[0]}</span>
            <button className="mobile-logout-btn" onClick={onLogout}>
              Sign Out
            </button>
          </div>
        </div>

        {/* Date picker + Map/List toggle */}
        <div className="mobile-controls">
          <DatePicker
            selectedDate={selectedDate}
            onDateChange={onDateChange}
          />
          <div className="mobile-toggle">
            <button
              className={`mobile-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              List
            </button>
            <button
              className={`mobile-toggle-btn ${viewMode === 'map' ? 'active' : ''}`}
              onClick={() => setViewMode('map')}
            >
              Map
            </button>
          </div>
        </div>
      </header>

      {/* Notifications */}
      {error && (
        <div className="mobile-notification mobile-notification--error">
          {error}
          <button onClick={onDismissError}>x</button>
        </div>
      )}

      {bookingSuccess && (
        <div className="mobile-notification mobile-notification--success">
          <strong>Booked!</strong> {bookingSuccess.roomName}
          <button onClick={onDismissSuccess}>OK</button>
        </div>
      )}

      {/* Main content */}
      <main className="mobile-main">
        {viewMode === 'list' ? (
          <RoomList
            availability={availability}
            onRoomSelect={onRoomSelect}
          />
        ) : (
          <div className="mobile-map-container">
            <FloorPlan
              officeConfig={officeConfig}
              availability={availability}
              onRoomSelect={onRoomSelect}
              selectedRoom={selectedRoom}
              isKiosk={false}
            />
          </div>
        )}
      </main>

      {/* Booking Panel - slides up from bottom on mobile */}
      {selectedRoom && (
        <div className="mobile-booking-overlay">
          <div className="mobile-booking-sheet">
            <BookingPanel
              selectedRoomId={selectedRoom}
              availability={availability}
              onBook={onBook}
              onCancel={() => onRoomSelect?.(null)}
              isKiosk={false}
              existingBooking={existingBooking}
              user={user}
              directBooking={directBooking}
              selectedDate={selectedDate}
            />
          </div>
        </div>
      )}
    </div>
  );
}
